import React, { useEffect, useState } from "react";
import { navItems } from "../../config/site";
import { assets } from "../../content/site";
import { trackEvent } from "../../lib/analytics";

type HeaderProps = {
    activeSection: string;
    onNavigate: (id: string) => void;
};

export function Header({ activeSection, onNavigate }: HeaderProps) {
    const [menuOpen, setMenuOpen] = useState(false);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? "hidden" : "";

        return () => {
            document.body.style.overflow = "";
        };
    }, [menuOpen]);

    const handleNavigate = (id: string) => {
        setMenuOpen(false);
        trackEvent("nav_section_click", { section: id, source: "header" });
        onNavigate(id);
    };

    return (
        <>
            <header
                className="sticky top-0 z-20 border-b border-white/25 px-4 py-4 sm:px-6 sm:py-3"
                style={{
                    background: "rgba(0, 0, 0, 0.64)",
                    backdropFilter: "blur(14.800000190734863px)",
                }}
            >
                <div className="mx-auto flex min-h-[31px] w-full max-w-[1400px] items-center justify-between gap-4">
                    <button
                        type="button"
                        className="shrink-0"
                        onClick={() => handleNavigate("top")}
                        aria-label="Наверх"
                    >
                        <img
                            src={assets.logo}
                            alt="DriveeCon"
                            className="w-[230px] max-[639px]:w-[129.432px]"
                        />
                    </button>
                    <nav
                        className="flex items-center gap-5 max-xl:hidden"
                        aria-label="Основная навигация"
                    >
                        {navItems.map((item) => {
                            const isActive = activeSection === item.id;

                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    className="group relative py-3 text-base leading-7 text-white"
                                    onClick={() => handleNavigate(item.id)}
                                >
                                    {item.label}
                                    <span
                                        className={[
                                            "absolute inset-x-0 bottom-1 transition-all",
                                            isActive
                                                ? "h-1.5 bg-accent"
                                                : "h-1 bg-transparent group-hover:bg-accent",
                                        ].join(" ")}
                                    />
                                </button>
                            );
                        })}
                    </nav>
                    <button
                        type="button"
                        className="hidden size-6 items-center justify-center text-white max-xl:inline-flex"
                        aria-label={menuOpen ? "Закрыть меню" : "Открыть меню"}
                        aria-expanded={menuOpen}
                        onClick={() => {
                            if (!menuOpen) {
                                trackEvent("mobile_menu_open");
                            }

                            setMenuOpen((prev) => !prev);
                        }}
                    >
                        {menuOpen ? (
                            <svg viewBox="0 0 24 24" fill="none" className="size-6">
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                            </svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="none" className="size-6">
                                <path
                                    d="M4 7H20M4 12H20M4 17H20"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                            </svg>
                        )}
                    </button>
                    <a
                        className="inline-flex h-[54px] w-[200px] items-center justify-center gap-4 rounded-[100px] border border-accent bg-accent px-6 py-4 whitespace-nowrap text-[18px] leading-none font-semibold tracking-[0] text-[#1e1e1e] shadow-[inset_0_0_0_1px_#96ea28] transition hover:-translate-y-0.5 hover:bg-[#abff40] hover:shadow-[0_12px_30px_rgba(150,234,40,0.22)] shrink-0 max-xl:ml-auto max-[639px]:hidden"
                        href="#recap"
                        onClick={(event) => {
                            event.preventDefault();
                            trackEvent("cta_recap_click", { source: "header" });
                            handleNavigate("recap");
                        }}
                    >
                        Как это было
                    </a>
                </div>
            </header>
            {menuOpen ? (
                <div className="fixed inset-0 z-30 bg-black/95 transition duration-300 xl:hidden">
                    <div className="absolute top-0 h-full w-full overflow-hidden bg-accent px-4 pb-8 pt-6 text-black transition-transform duration-300 translate-y-0">
                        <button
                            type="button"
                            className="absolute right-4 top-4 inline-flex size-8 items-center justify-center text-black"
                            aria-label="Закрыть меню"
                            onClick={() => setMenuOpen(false)}
                        >
                            <svg viewBox="0 0 24 24" fill="none" className="size-7">
                                <path
                                    d="M18 6L6 18M6 6L18 18"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </button>
                        <nav aria-label="Мобильная навигация">
                            {navItems.map((item, index) => (
                                <button
                                    key={item.id}
                                    type="button"
                                    className={[
                                        "flex w-full items-center border-black py-[12px] text-left text-[18px] leading-none font-medium uppercase tracking-[-0.04em]",
                                        index < navItems.length - 1 ? "border-b" : "",
                                    ].join(" ")}
                                    onClick={() => handleNavigate(item.id)}
                                >
                                    {item.label}
                                </button>
                            ))}
                        </nav>
                        <div className="absolute inset-x-4 bottom-8 flex flex-col items-center gap-3 text-center">
                            <a
                                href="mailto:rungothecat@gmail.com"
                                className="text-[12px] leading-7 text-black no-underline"
                            >
                                rungothecat@gmail.com
                            </a>
                            <a
                                href="tel:+79241744752"
                                className="text-[12px] leading-7 text-black no-underline"
                            >
                                +7 (924) 174-47-52
                            </a>
                            <a
                                className="relative inline-flex h-[35px] items-center justify-center rounded-[37px] border border-black px-[13px] py-1 text-[16px] leading-7 font-medium uppercase tracking-[-0.04em] text-black no-underline"
                                href="https://t.me/driveecon"
                                target="_blank"
                                rel="noreferrer"
                                onClick={() =>
                                    trackEvent("cta_subscribe_click", { source: "mobile_menu" })
                                }
                            >
                                подпишись на канал
                            </a>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
}
